import { Injectable, Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggingService } from '@logging/logging.service';
import { BroadcastService } from '@utils/broadcast.service';
import { ActiveAuthenticationStateService } from './active-authentication.state-service';
import { authenticationRoutePaths } from '../../app/app-layout/authentication/authentication-route-paths';
import { AuthStateUtils } from './auth-state.utils';
import { finalize, Observable } from 'rxjs';
import { AuthState } from './state/models/auth.models';
import { AuthorisationDataProvider } from './state/authentication.data-provider';
import { map, catchError, throwError, tap, switchMap } from 'rxjs';
import { LogType } from '@logging/log';
import { ActiveSecurityContextStateService } from './active-security-context.state-service';
import { UnityCookieService } from '@utils/unity-cookie.service';
import { ActiveUserDataProvider } from './state/active-user.data-provider';
import { ActiveUser } from './state/models/active-user.model';

/**
 * Service for managing the authenticated models of the current user.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private router: Router | null = null;
  private route: ActivatedRoute | null = null;

  private initialUrl: string | undefined;

  setInitialUrl(url: string) {
    if (this.initialUrl === null) {
      this.initialUrl = url;
    }
  }

  constructor(
    private injector: Injector, // Router not available from an APP_INITIALIZER (see AuthenticationSetupService), so will locate it directly
    private authenticationState: ActiveAuthenticationStateService,
    private broadcastService: BroadcastService,
    private logger: LoggingService,
    private authorisationDataProvider: AuthorisationDataProvider,
    private activeSecurityContextState: ActiveSecurityContextStateService,
    private cookieService: UnityCookieService,
    private activeUserDataProvider: ActiveUserDataProvider,
    private unityCookieService: UnityCookieService
  ) {
    this.handleSignOutOnMultipleTabs();
  }

  /**
   * Ensures that when a user signs-out, the event is broadcast to other tabs so they can redirect
   */
  private handleSignOutOnMultipleTabs() {
    const signOutTopic = 'sign-out';
    this.broadcastService.on(signOutTopic, () => {
      this.setActiveIdentity(undefined);
    });
  }

  public setActiveIdentity(user: ActiveUser | undefined) {
    if(!user) {
      this.authenticationState.activeIdentity = undefined;
      return;
    }
    this.authenticationState.activeIdentity = user;
  }

  /**
   * possible to arrive here during an APP_INITIALIZER, so we need to pull the Router from the Injector directly
   */
  private ensureRouter() {
    if (!this.router) this.router = this.injector.get<Router>(Router);
    if (!this.route) this.route = this.injector.get<ActivatedRoute>(ActivatedRoute);
  }

  navigateToSignIn(force = true, signingOut = false) {
    this.ensureRouter();

    // only redirect if someone hasn't landed on an anon route e.g. reset password
    const isAnAuthRoute = this.initialUrl?.includes('auth/');
    // remember the app entry path to redirect to after successful sign-in
    if (!isAnAuthRoute && !signingOut) this.authenticationState.landingRoute = this.initialUrl;
    // /auth routes target the auxiliary outlet 'top' in app.component
    if ((!isAnAuthRoute || force) && this.router)
      this.router.parseUrl(authenticationRoutePaths.signIn);
  }

  userSignIn$(email: string, password: string, rememberMe: boolean): Observable<AuthState> {
    this.authenticationState.inFlight = true;
    this.clearDataFormLogin();
    this.setRememberEmail(rememberMe ? email : null);
    return this.authorisationDataProvider.signIn$(email, password, rememberMe).pipe(
      switchMap((response: AuthState) => {
        if (response.success === true) {
          return this.completeSuccessfulSignIn$(response);
        }
        const returnError =
          response.errorMessages.length > 0
            ? response.errorMessages.map((err) => err.message).join(', ')
            : 'There has been an error logging in, please check your email and password are correct.';
        this.authenticationState.inFlight = false;
        return throwError(() => new Error(returnError));
      }),
      catchError((error) => {
        this.authenticationState.inFlight = false;
        return throwError(() => error);
      })
    );
  }

  /**
   * Sign in using a Microsoft authentication token
   * @param token The Microsoft authentication token
   * @returns Observable of AuthState
   */
  userSignInByToken$(token: string): Observable<AuthState> {
    this.authenticationState.inFlight = true;
    this.clearDataFormLogin();
    return this.authorisationDataProvider.signInByToken$(token).pipe(
      switchMap((response: AuthState) => {
        if (response.success === true) {
          return this.completeSuccessfulSignIn$(response);
        }
        const returnError =
          response.errorMessages.length > 0
            ? response.errorMessages.map((err) => err.message).join(', ')
            : 'There has been an error authenticating with Microsoft. Please try again.';
        this.authenticationState.inFlight = false;
        return throwError(() => new Error(returnError));
      }),
      catchError((error) => {
        this.authenticationState.inFlight = false;
        return throwError(() => error);
      })
    );
  }

  /**
   * Applies auth state/cookies after a successful sign-in API response, then loads the active user.
   * Errors from {@link fetchActiveUser$} propagate to the caller (e.g. {@link userSignIn$} / {@link userSignInByToken$}).
   */
  private completeSuccessfulSignIn$(response: AuthState): Observable<AuthState> {
    this.logger.trace('AUTH: user is now authenticated.');
    AuthStateUtils.isFreshAuthentication = true;
    this.activeSecurityContextState.setActiveAuthState(response);
    this.cookieService.setAuthState(response);
    return this.fetchActiveUser$(response.userId, response.tenantId).pipe(
      map(() => response),
      finalize(() => {
        this.authenticationState.inFlight = false;
      })
    );
  }

  clearDataFormLogin() {
    this.unityCookieService.clearAuthState();
    this.cookieService.deleteCookie('selectedMPID');
    this.cookieService.deleteCookie('myEmail');
  }

  setRememberEmail(email: string | null) {
    if(!email) 
        this.cookieService.deleteCookie('myEmail');
    else
        this.cookieService.setCookie('myEmail', email);     
  }

  /**
   * Loads the active user from the API and updates {@link ActiveAuthenticationStateService}.
   * All callers should use this instead of {@link ActiveUserDataProvider.fetchActiveUser$} directly.
   */
  fetchActiveUser$(userId: string, tenantId: string): Observable<ActiveUser> {
    return this.activeUserDataProvider.fetchActiveUser$(userId, tenantId).pipe(
      tap((user) => this.setActiveIdentity(user)),
      catchError((error) => {
        this.logger.error(error, { type: LogType.Error });
        return throwError(() => error);
      })
    );
  }

  /**
   * Sign-out the current user and navigate to the login form
   */ 
  signOutAndRedirect = () => {
    this.signOut()
      .then(() => {
        this.logger.trace('AUTH: user was signed out');
        this.unityCookieService.clearAuthState();
        this.authenticationState.landingRoute = undefined;
        this.setActiveIdentity(undefined);
      })
      .then(() => {
        // Always use sign-in: `document.referrer` is from the initial document load, not the current SPA
        // route, and can be empty (then resolving to the same deep-linked URL) or an unrelated site.
        window.location.href = new URL(authenticationRoutePaths.signIn, window.location.origin).toString();
      });
  };

  signOut(): Promise<void> {
    //TODO do we need an endpoint for expiring any tokens at the server level?
    this.unityCookieService.clearAuthState();
    this.activeSecurityContextState.clearActiveAuthState();
    this.authenticationState.landingRoute = undefined;
    this.authenticationState.activeIdentity = undefined;
    return Promise.resolve();
  }

}
